import { motion } from "framer-motion";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import CTABand from "../components/sections/CTABand";
import {
    CREDENTIALS,
    STANDARDS,
    ABOUT_STORY,
    FOUNDER,
} from "../data/about";

// -----------------------------------------------------------------------------
// FounderPortrait — <picture> with AVIF + WebP + JPEG sources at 3 widths,
// hydrated to a subtle warm vignette overlay via ::after so the man is never
// obscured. Assets emitted by scripts under /public/images/team/.
// -----------------------------------------------------------------------------
function FounderPortrait({ alt }) {
    const base = "/images/team/rusty-white";
    const widths = [480, 768, 960];
    // We fetch the LCP-ish portrait eagerly since it sits above the fold on
    // the About page. Aspect ratio 4:5 matches the crop the build script did.
    return (
        <div
            className="relative overflow-hidden rounded-[14px] border border-[rgba(201,168,106,0.2)] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.55)]"
            data-testid="founder-portrait"
        >
            <picture>
                <source
                    type="image/avif"
                    srcSet={widths
                        .map((w) => `${base}-${w}.avif ${w}w`)
                        .join(", ")}
                    sizes="(max-width: 768px) 90vw, 480px"
                />
                <source
                    type="image/webp"
                    srcSet={widths
                        .map((w) => `${base}-${w}.webp ${w}w`)
                        .join(", ")}
                    sizes="(max-width: 768px) 90vw, 480px"
                />
                <img
                    src={`${base}-768.jpg`}
                    srcSet={widths
                        .map((w) => `${base}-${w}.jpg ${w}w`)
                        .join(", ")}
                    sizes="(max-width: 768px) 90vw, 480px"
                    width={768}
                    height={960}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    alt={alt}
                    className="block w-full h-auto"
                />
            </picture>
            {/* Warm vignette — champagne wash at top-right, cinematic fade at bottom */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(120% 90% at 90% 5%, rgba(201,168,106,0.14) 0%, rgba(201,168,106,0) 55%), linear-gradient(to bottom, rgba(11,11,12,0) 55%, rgba(11,11,12,0.55) 100%)",
                }}
            />
            {/* Thin champagne accent bar tucked at the bottom edge */}
            <div
                aria-hidden="true"
                className="absolute bottom-6 left-6 h-px w-14 bg-[#C9A86A]"
            />
        </div>
    );
}

export default function About() {
    // Person JSON-LD for Rusty. Kept static — no client-only reads that
    // could trip the Phase 2 hydration guard.
    const founderSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        name: FOUNDER.name,
        jobTitle: FOUNDER.title,
        worksFor: {
            "@type": "Organization",
            name: "Advanced Connected Technologies",
        },
        image: "https://actluxe.net/images/team/rusty-white.jpg",
        description: FOUNDER.schemaDescription,
    };

    return (
        <div data-testid="about-page">
            <SEO
                title="About — Advanced Connected Technologies"
                bare
                description="Founded by Rusty White — active U.S. Army Reserves Staff Sergeant and Squad Leader — Advanced Connected Technologies brings military-grade discipline and craftsmanship to luxury residential technology."
                canonical="https://actluxe.net/about"
            />
            <StructuredData data={founderSchema} />

            {/* Hero */}
            <section className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-16 md:pb-24 border-b border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <SectionLabel number="About" label="Our House" />
                    <h1 className="mt-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
                        {"Craftsmen of the "}
                        <span className="italic text-[#C9A86A]">
                            Connected Home.
                        </span>
                    </h1>
                </div>
            </section>

            {/* Founder Feature — placed high, right below the hero */}
            <section
                className="section-pad bg-[#0B0B0C] border-b border-[rgba(201,168,106,0.12)] overflow-hidden"
                data-testid="about-founder"
            >
                <div className="shell">
                    <SectionLabel number="The Founder" label="Behind the Craft" />
                    <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 lg:gap-20 items-start">
                        <div className="md:col-span-5 md:sticky md:top-24">
                            <FounderPortrait alt={FOUNDER.alt} />
                        </div>
                        <div className="md:col-span-7">
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-[56px] text-[#F3EFE7] leading-[1.08]">
                                {FOUNDER.name}
                            </h2>
                            <p
                                className="mt-3 text-[11px] tracking-[0.28em] uppercase text-[#C9A86A]"
                                data-testid="founder-title"
                            >
                                {FOUNDER.title}
                            </p>
                            {/* Credentials pills */}
                            <div
                                className="mt-6 flex flex-wrap gap-2.5"
                                data-testid="founder-credentials"
                            >
                                {FOUNDER.credentials.map((c) => (
                                    <span
                                        key={c}
                                        className="inline-block text-[10.5px] tracking-[0.24em] uppercase text-[#F3EFE7] border border-[rgba(201,168,106,0.35)] bg-[#15161A] px-3 py-1.5"
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>

                            {/* Bio paragraphs — using text prefix as stable key so
                                a future edit to the bio content doesn't churn
                                the paragraph nodes across renders. */}
                            <div className="mt-8 space-y-6 text-[#F3EFE7] text-[17px] md:text-[18px] leading-[1.85]">
                                {FOUNDER.bio.map((para, idx) => (
                                    <p
                                        key={para.text.slice(0, 40)}
                                        className={
                                            para.emphasis
                                                ? "font-serif italic text-[#C9A86A] text-xl md:text-2xl leading-[1.5]"
                                                : idx === 1
                                                ? "text-[#9A958A]"
                                                : ""
                                        }
                                    >
                                        {para.text}
                                    </p>
                                ))}
                            </div>

                            {/* Pull quote */}
                            <motion.blockquote
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{
                                    duration: 0.9,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="mt-10 border-l-2 border-[#C9A86A] pl-6 py-2"
                                data-testid="founder-quote"
                            >
                                <p className="font-serif italic text-2xl md:text-[28px] text-[#F3EFE7] leading-[1.4]">
                                    {`\u201C${FOUNDER.quote}\u201D`}
                                </p>
                                <footer className="mt-4 text-[11px] tracking-[0.28em] uppercase text-[#9A958A]">
                                    — {FOUNDER.name}, Founder
                                </footer>
                            </motion.blockquote>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="section-pad bg-[#0B0B0C] overflow-hidden">
                <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
                    <div className="lg:col-span-7">
                        <SectionLabel number="01" label="Our Story" />
                        <div className="mt-8 space-y-6 text-[#F3EFE7] text-lg md:text-[19px] leading-[1.85]">
                            <p>{ABOUT_STORY.paragraph1}</p>
                            <p className="text-[#9A958A]">
                                {ABOUT_STORY.paragraph2}
                            </p>
                        </div>
                    </div>
                    <aside className="lg:col-span-5 lg:pl-12 border-l border-[rgba(201,168,106,0.2)] flex items-center">
                        <motion.blockquote
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="font-serif italic text-3xl md:text-4xl text-[#C9A86A] leading-[1.25]"
                            data-testid="about-pullquote"
                        >
                            {`\u201C${ABOUT_STORY.pullquote}\u201D`}
                        </motion.blockquote>
                    </aside>
                </div>
            </section>

            {/* Credentials */}
            <section
                className="section-pad bg-[#15161A] border-y border-[rgba(201,168,106,0.12)] overflow-hidden"
                data-testid="about-credentials"
            >
                <div className="shell">
                    <div className="flex flex-col items-center text-center mb-12 md:mb-16">
                        <SectionLabel
                            number="02"
                            label="Credentials & Certifications"
                            align="center"
                        />
                        <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.08]">
                            {"Held current, "}
                            <span className="italic text-[#C9A86A]">
                                across every platform we install.
                            </span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                        {CREDENTIALS.map((c, i) => (
                            <motion.div
                                key={c}
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{
                                    duration: 0.6,
                                    delay: i * 0.04,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="border border-[rgba(201,168,106,0.25)] bg-[#0B0B0C]/40 px-4 py-5 md:py-6 text-center transition-all duration-500 hover:border-[rgba(201,168,106,0.65)] hover:bg-[#0B0B0C]/70"
                                data-testid={`credential-${i}`}
                            >
                                <span className="text-[11.5px] tracking-[0.22em] uppercase text-[#F3EFE7]">
                                    {c}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Standard */}
            <section className="section-pad bg-[#15161A] border-t border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-end mb-14 md:mb-20">
                        <div className="lg:col-span-7">
                            <SectionLabel number="03" label="Our Standard" />
                            <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08]">
                                {"Four words, "}
                                <span className="italic text-[#C9A86A]">
                                    held to.
                                </span>
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(201,168,106,0.15)] border border-[rgba(201,168,106,0.15)]">
                        {STANDARDS.map((s, i) => (
                            <motion.div
                                key={s.title}
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.7,
                                    delay: i * 0.07,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="bg-[#15161A] p-10 md:p-14 transition-colors duration-500 hover:bg-[#1C1D22]"
                                data-testid={`standard-${i}`}
                            >
                                <span className="eyebrow">0{i + 1}</span>
                                <h3 className="mt-5 font-serif text-3xl md:text-4xl text-[#F3EFE7] mb-4">
                                    {s.title}
                                </h3>
                                <p className="text-[#9A958A] text-[15px] md:text-base leading-[1.85]">
                                    {s.copy}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <CTABand />
        </div>
    );
}
